import type { TicketAttendeeInput } from "./ticketingApi";

export type AttendeeRow = TicketAttendeeInput;
export type AttendeeRowErrors = { name?: string; email?: string };

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Grows or shrinks the rows to `quantity`, keeping every row that survives.
 * Rows removed by shrinking are remembered in `stash` so growing back restores
 * what was typed.
 */
export function resizeAttendees(rows: AttendeeRow[], stash: AttendeeRow[], quantity: number) {
  const all = [...rows, ...stash];
  const next = all.slice(0, quantity);
  while (next.length < quantity) next.push({ name: "", email: "" });
  return { rows: next, stash: all.slice(quantity) };
}

export function validateAttendees(rows: AttendeeRow[]): AttendeeRowErrors[] {
  return rows.map(row => {
    const errors: AttendeeRowErrors = {};
    // Spec 3.4 copy. Duplicate emails (incl. the buyer's) are allowed (D4).
    if (!row.name.trim()) errors.name = "Enter a name.";
    if (!EMAIL_RE.test(row.email.trim())) errors.email = "Enter a valid email.";
    return errors;
  });
}

export function isValidEmail(email: string) {
  return EMAIL_RE.test(email.trim());
}

export function countErrors(errors: AttendeeRowErrors[]) {
  return errors.reduce((n, e) => n + (e.name ? 1 : 0) + (e.email ? 1 : 0), 0);
}

export function hasErrors(errors: AttendeeRowErrors[]) {
  return errors.some(e => e.name || e.email);
}

/* ── Cancel-restore storage ─────────────────────────────────────────────── */

export const TICKET_DRAFT_TTL_MS = 24 * 60 * 60 * 1000;
export const ticketDraftKey = (slug: string) => `chi-ticket-draft:${slug}`;

type TicketDraft = { v: 1; savedAt: number; quantity: number; attendees: AttendeeRow[]; checkoutGroupId?: string };

function storage(): Storage | null {
  try {
    return typeof window !== "undefined" ? window.sessionStorage : null;
  } catch {
    return null;
  }
}

export function saveTicketDraft(slug: string, attendees: AttendeeRow[], checkoutGroupId?: string) {
  const draft: TicketDraft = { v: 1, savedAt: Date.now(), quantity: attendees.length, attendees, ...(checkoutGroupId ? { checkoutGroupId } : {}) };
  try {
    storage()?.setItem(ticketDraftKey(slug), JSON.stringify(draft));
  } catch {
    /* storage full or blocked: cancel-restore is a convenience */
  }
}

/** Returns a valid, fresh draft or null; malformed or stale entries are dropped. */
export function loadTicketDraft(slug: string, maxQuantity = 50): { quantity: number; attendees: AttendeeRow[]; checkoutGroupId?: string } | null {
  const store = storage();
  if (!store) return null;
  let raw: string | null = null;
  try {
    raw = store.getItem(ticketDraftKey(slug));
  } catch {
    return null;
  }
  if (!raw) return null;
  try {
    const d = JSON.parse(raw) as Partial<TicketDraft>;
    const ok =
      d && d.v === 1 && typeof d.savedAt === "number" && Date.now() - d.savedAt <= TICKET_DRAFT_TTL_MS &&
      Number.isInteger(d.quantity) && (d.quantity as number) >= 1 && (d.quantity as number) <= maxQuantity &&
      Array.isArray(d.attendees) && d.attendees.length === d.quantity &&
      d.attendees.every(a => a && typeof a.name === "string" && typeof a.email === "string" && a.name.length <= 200 && a.email.length <= 320);
    if (!ok) throw new Error("bad draft");
    const checkoutGroupId = typeof d.checkoutGroupId === "string" && d.checkoutGroupId.length <= 64 ? d.checkoutGroupId : undefined;
    return { checkoutGroupId, quantity: d.quantity as number, attendees: (d.attendees as AttendeeRow[]).map(a => ({ name: a.name, email: a.email })) };
  } catch {
    clearTicketDraft(slug);
    return null;
  }
}

export function clearTicketDraft(slug: string) {
  try {
    storage()?.removeItem(ticketDraftKey(slug));
  } catch {
    /* ignore */
  }
}

/** Shared success path: once tickets are confirmed, every saved draft is obsolete. */
export function clearAllTicketDrafts() {
  const store = storage();
  if (!store) return;
  try {
    const keys: string[] = [];
    for (let i = 0; i < store.length; i++) {
      const k = store.key(i);
      if (k && k.startsWith("chi-ticket-draft:")) keys.push(k);
    }
    keys.forEach(k => store.removeItem(k));
  } catch {
    /* ignore */
  }
}
